import { Box, Button } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { methodEditTailorGenerateTags, methodRemoveTemplate } from "../../../api/apitemp/api";
import { useEditButton } from "../../../component/basic/data_displays/buttons/edit_button";
import { BBDialog } from "../../../component/basic/feedbacks/dialog/BBDialog";
import { AlertSuccess } from "../../../component/basic/feedbacks/snackbar/Snackbar";
import { TagLabelsInput } from "../../../component/business/tag/Tags";
import { BookTemplate } from "../../../model/book/Book";
import {
    BookParamConstIDs,
    BookParamKind,
    BookParamTailorGenerate,
} from "../../../model/book/BookTemplate";
import { Tag } from "../../../model/TagData";
import { useAppDispatch } from "../../../store/hooks";
import { getTagsFromBank, selectTagBank } from "../../../store/root/selector";
import { ColorAdmin } from "../../../tools/color";
import { RefreshPage } from "../../../tools/url";

interface BookAdminBoxProps {
    temp: BookTemplate;
    height?: number;
    onRefresh?: () => void;
}

function BookAdminBox(props: BookAdminBoxProps) {
    const dispatch = useAppDispatch();

    const { height, temp, onRefresh } = props;

    const [deleteDialog, setDeleteDialog] = useState(false);

    return (
        <Box
            sx={{
                border: "2px solid",
                borderColor: ColorAdmin,
                mt: "5px",
                p: "10px",
                ...(height !== undefined ? { height: height } : {}),
            }}
        >
            {temp.param.type === BookParamKind.ConstIDs && (
                <ConstIDsBox param={temp.param.const_ids} />
            )}
            {temp.param.type === BookParamKind.TailorGenerate && (
                <TailorGenerateBox param={temp.param.tailor_generate} tempID={temp.id} />
            )}
            {temp.unlock_date_stamp > 0 && (
                <Box>解锁日期: {dayjs.unix(temp.unlock_date_stamp).format("YYYY/MM/DD HH:mm")}</Box>
            )}
            <Button
                variant="contained"
                color="bbdisable"
                size="small"
                sx={{ mt: "5px" }}
                onClick={() => {
                    setDeleteDialog(true);
                }}
            >
                删除
            </Button>
            <BBDialog
                open={deleteDialog}
                setOpen={setDeleteDialog}
                content={
                    <>
                        删除练习册 <b>{temp.title + " " + temp.sub_title}</b>, 确认?
                    </>
                }
                onConfirm={() => {
                    methodRemoveTemplate(dispatch, {
                        id: temp.id,
                    }).then(() => {
                        setDeleteDialog(false);
                        AlertSuccess("执行成功");
                        if (onRefresh) {
                            onRefresh();
                        } else {
                            RefreshPage();
                        }
                    });
                }}
            />
        </Box>
    );
}

interface ConstIDsBoxProps {
    param: BookParamConstIDs;
}
function ConstIDsBox(props: ConstIDsBoxProps) {
    const { param } = props;

    const tagBank = useSelector(selectTagBank);

    return (
        <>
            <Box>题目数量: {param.ids.length}</Box>
            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                标签:&nbsp;
                {param.tag_ids.map((tid, index) => {
                    const tag = tagBank.tagMap.get(tid);
                    return <div key={index}>{tag ? <span>[{tag.label}]</span> : <></>}</div>;
                })}
            </Box>
        </>
    );
}

interface TailorGenerateBoxProps {
    param: BookParamTailorGenerate;
    tempID: string;
}
function TailorGenerateBox(props: TailorGenerateBoxProps) {
    const dispatch = useAppDispatch();

    const { param, tempID } = props;

    const tagBank = useSelector(selectTagBank);

    const [selectTags, setSelectTags] = useState<Tag[]>([]);

    useEffect(() => {
        setSelectTags(getTagsFromBank(param.tag_ids, tagBank));
    }, [tagBank, param]);

    const [buttons, edit] = useEditButton(
        () => {
            methodEditTailorGenerateTags(dispatch, {
                id: tempID,
                tag_ids: selectTags.map((t) => t.id),
            }).then(() => {
                AlertSuccess("修改成功");
                RefreshPage();
            });
        },
        () => {
            setSelectTags(getTagsFromBank(param.tag_ids, tagBank));
        },
        true
    );
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            {edit ? (
                <TagLabelsInput
                    tagsAll={tagBank.tags}
                    value={selectTags}
                    setValue={setSelectTags}
                />
            ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    标签:&nbsp;
                    {param.tag_ids.map((tid, index) => {
                        const tag = tagBank.tagMap.get(tid);
                        return <div key={index}>{tag ? <span>[{tag.label}]</span> : <></>}</div>;
                    })}
                </Box>
            )}
            {buttons}
        </Box>
    );
}

export default BookAdminBox;
